import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import criticalInfrastructureService from '../../services/criticalInfrastructureService';

import InfrastructureHealthCard from './components/InfrastructureHealthCard';
import ServiceTopologyPanel from './components/ServiceTopologyPanel';
import PerformanceGraphsPanel from './components/PerformanceGraphsPanel';
import AlertManagementPanel from './components/AlertManagementPanel';
import AutomatedRemediationPanel from './components/AutomatedRemediationPanel';
import toast, { Toaster } from 'react-hot-toast';

const CriticalInfrastructureMonitoringAlertCommandCenter = () => {
  const [activeTab, setActiveTab] = useState('service_topology');
  const [serviceHealth, setServiceHealth] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [remediationLog, setRemediationLog] = useState([]);

  const handleNewAlert = (newAlert, eventType) => {
    if (eventType === 'INSERT') {
      setAlerts(prev => [newAlert, ...prev]);
      toast?.error(`New ${newAlert?.severity} alert: ${newAlert?.title}`);
    } else if (eventType === 'UPDATE') {
      setAlerts(prev => prev?.map(alert => alert?.id === newAlert?.id ? newAlert : alert));
    }
  };

  useEffect(() => {
    loadDashboardData();

    // Real-time alert subscription
    const channel = criticalInfrastructureService?.subscribeToInfrastructureAlerts(handleNewAlert);

    return () => {
      criticalInfrastructureService?.unsubscribeFromInfrastructureAlerts(channel);
    };
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadDashboardData();
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [services, activeAlerts, dbPerf, apiPerf, networkMetrics] = await Promise.all([
        criticalInfrastructureService?.getServiceHealthStatus(),
        criticalInfrastructureService?.getActiveAlerts(),
        criticalInfrastructureService?.getDatabasePerformance(),
        criticalInfrastructureService?.getApiGatewayMetrics(),
        criticalInfrastructureService?.getNetworkMetrics()
      ]);

      setServiceHealth(services || []);
      setAlerts(activeAlerts || []);
      setPerformanceMetrics({
        database: dbPerf,
        api: apiPerf,
        network: networkMetrics
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast?.error('Failed to load infrastructure data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = async (alertData) => {
    try {
      await criticalInfrastructureService?.createInfrastructureAlert(alertData);
      toast?.success('Alert created successfully');
      loadDashboardData();
    } catch (error) {
      console.error('Error creating alert:', error);
      toast?.error('Failed to create alert');
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await criticalInfrastructureService?.updateAlertStatus(alertId, 'acknowledged');
      toast?.success('Alert acknowledged');
      loadDashboardData();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      toast?.error('Failed to acknowledge alert');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await criticalInfrastructureService?.updateAlertStatus(alertId, 'resolved');
      toast?.success('Alert resolved');
      loadDashboardData();
    } catch (error) {
      console.error('Error resolving alert:', error);
      toast?.error('Failed to resolve alert');
    }
  };

  const handleEscalateAlert = async (alertId) => {
    try {
      await criticalInfrastructureService?.escalateAlert(alertId);
      toast?.success('Alert escalated to next level');
      loadDashboardData();
    } catch (error) {
      console.error('Error escalating alert:', error);
      toast?.error('Failed to escalate alert');
    }
  };

  const handleExecuteRemediation = async (protocolType, parameters) => {
    try {
      toast?.loading(`Executing ${protocolType?.replace(/_/g, ' ')}...`);

      const result = await criticalInfrastructureService?.executeRemediationProtocol(protocolType, parameters);
      
      setRemediationLog(prev => [{
        protocol: protocolType,
        result,
        timestamp: new Date()?.toISOString()
      }, ...prev]);

      toast?.dismiss();
      toast?.success(result?.message || 'Remediation executed successfully');
    } catch (error) {
      console.error('Remediation execution error:', error);
      toast?.dismiss();
      toast?.error('Failed to execute remediation');
    }
  };

  const tabs = [
    { id: 'service_topology', label: 'Service Topology', icon: 'Network' },
    { id: 'performance_graphs', label: 'Performance Graphs', icon: 'TrendingUp' },
    { id: 'alert_management', label: 'Alert Management', icon: 'Bell' },
    { id: 'automated_remediation', label: 'Automated Remediation', icon: 'Wrench' }
  ];

  const criticalAlerts = alerts?.filter(a => a?.severity === 'critical')?.length || 0;
  const operationalServices = serviceHealth?.filter(s => s?.status === 'operational')?.length || 0;

  return (
    <>
      <Helmet>
        <title>Critical Infrastructure Monitoring & Alert Command Center</title>
      </Helmet>
      <Toaster position="top-right" />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <Icon name="Shield" className="w-8 h-8 text-blue-600" />
                  Critical Infrastructure Command Center
                </h1>
                <p className="text-gray-600 mt-1">Real-time monitoring with intelligent alerting and automated escalation</p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  variant="outline"
                  className={autoRefresh ? 'border-green-500 text-green-600' : ''}
                >
                  <Icon name={autoRefresh ? 'Play' : 'Pause'} className="w-4 h-4 mr-2" />
                  {autoRefresh ? 'Auto-Refresh On' : 'Auto-Refresh Off'}
                </Button>
                <Button
                  onClick={loadDashboardData}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Icon name="RefreshCw" className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            {/* Infrastructure Health Overview - 4 cols */}
            <div className="col-span-12 lg:col-span-4">
              <InfrastructureHealthCard
                serviceHealth={serviceHealth}
                alerts={alerts}
                performanceMetrics={performanceMetrics}
                loading={loading}
              />
            </div>

            {/* Main Monitoring Panel - 8 cols */}
            <div className="col-span-12 lg:col-span-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <div className="flex overflow-x-auto">
                    {tabs?.map(tab => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`px-6 py-4 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                          activeTab === tab?.id
                            ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                        }`}
                      >
                        <Icon name={tab?.icon} className="w-4 h-4" />
                        {tab?.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'service_topology' && (
                    <ServiceTopologyPanel
                      serviceHealth={serviceHealth}
                      onAnalyzeCascadeRisk={criticalInfrastructureService?.analyzeCascadeRisk}
                    />
                  )}

                  {activeTab === 'performance_graphs' && (
                    <PerformanceGraphsPanel
                      performanceMetrics={performanceMetrics}
                    />
                  )}

                  {activeTab === 'alert_management' && (
                    <AlertManagementPanel
                      alerts={alerts}
                      onAcknowledge={handleAcknowledgeAlert}
                      onResolve={handleResolveAlert}
                      onEscalate={handleEscalateAlert}
                      onCreateAlert={handleCreateAlert}
                    />
                  )}

                  {activeTab === 'automated_remediation' && (
                    <AutomatedRemediationPanel
                      remediationLog={remediationLog}
                      onExecuteRemediation={handleExecuteRemediation}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-sm text-gray-700">
                    {operationalServices}/{serviceHealth?.length} Services Operational
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-gray-700">
                    {criticalAlerts} Critical Alerts
                  </span>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Last updated: {new Date()?.toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CriticalInfrastructureMonitoringAlertCommandCenter;
